import React, { useState, useRef, useCallback, useEffect, useMemo, useDeferredValue, startTransition } from 'react';
import { debounce } from 'lodash';
import { AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Upload, FolderOpen, Plus, SearchX } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useProjectContext } from '../../context/ProjectContext';
import { useCreateFile, useUpdateFile, useDeleteFile } from '../../hooks/useProjectQueries';
import { FilesSkeleton } from '../../components/skeletons/PageSkeletons';
import { useFileSelection } from '../../hooks/useFileSelection';
import { Surface } from '../../components/layout/Surface';
import { ArtifactSurface } from '../../components/layout/ArtifactSurface';
import { FilePanel } from '../../components/files/FilePanel';
import FileControlBar, { EMPTY_FILTERS, filterAndSortFiles } from '../../components/files/FileControlBar';
import FileCard from '../../components/files/FileCard';
import FileRow from '../../components/files/FileRow';
import FileShelf from '../../components/files/FileShelf';
import FileBulkActions from '../../components/files/FileBulkActions';
import { getFileIcon, categoriesFor, collectTags, isChatUpload, isDraft, isDraftAttachment } from '../../components/files/fileMeta';
import { DRAFTS_CATEGORY } from '../../components/drafts/draftModel';

/**
 * Artifacts.
 *
 * This route used to open with an <h1>Files</h1> and a "24 artifacts · 3 pinned"
 * subtitle, which made it the last titled page in the app — the nav rail already
 * says Artifacts, and the grid underneath already shows how many there are. The
 * header is now a FileControlBar, composed the way Tasks composes its board: a
 * bare flex column turning the shell's horizontal axis, with the bar and the
 * list as separate surfaces and the canvas showing through the gap between them.
 */

const EMPTY_FILES = [];

export default function ProjectFiles() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const { project, files: ctxFiles, isLoadingFiles, readOnly, baseUrl } = useProjectContext();
    const files = ctxFiles ?? EMPTY_FILES;

    const createFileMutation = useCreateFile(projectId);
    const updateFileMutation = useUpdateFile(projectId);
    const deleteFileMutation = useDeleteFile(projectId);

    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [sort, setSort] = useState('edited');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Creation state
    const [newFileName, setNewFileName] = useState('');
    const [newFileCategory, setNewFileCategory] = useState('Docs');
    const [uploadFile, setUploadFile] = useState(null);

    // The opened artifact. We hold an id rather than a file snapshot so React
    // Query updates (renames, tag edits, agent writes) flow straight through —
    // with a local draft layered on top so typing isn't fighting the 1s
    // debounce behind it.
    const [openFileId, setOpenFileId] = useState(null);
    const [draftContent, setDraftContent] = useState(null);
    const [isSavingFile, setIsSavingFile] = useState(false);

    // Drag and drop
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);

    // ─── Derived ────────────────────────────────────────────────────────────
    // Search reads file bodies, and `files` gets a fresh identity on every
    // optimistic autosave — so the query is deferred and the whole thing is one
    // pass in one memo, rather than the four chained filters and a sort it
    // replaces.
    const deferredQuery = useDeferredValue(search);
    const visibleFiles = useMemo(
        () => filterAndSortFiles(files, deferredQuery, filters, sort),
        [files, deferredQuery, filters, sort]
    );

    const orderedIds = useMemo(() => visibleFiles.map((f) => f.id), [visibleFiles]);
    const selection = useFileSelection(orderedIds);

    // Drafts only earn a category chip and a shelf column once you've asked to
    // see them. Leaving "Drafts" in the list while the filter hides every draft
    // would offer a toggle that visibly does nothing, and a permanently empty
    // column on the shelf.
    const allCategories = useMemo(() => categoriesFor(project), [project]);
    const categories = useMemo(
        () => (filters.showDrafts ? allCategories : allCategories.filter((c) => c.id !== DRAFTS_CATEGORY)),
        [allCategories, filters.showDrafts]
    );

    const tags = useMemo(() => collectTags(files), [files]);

    // One pass for both counters — this runs against every file on each render
    // of a route whose whole job is listing them.
    const { chatUploadCount, draftCount, totalCount } = useMemo(() => {
        let chat = 0;
        let drafts = 0;
        let total = 0;
        for (const f of files) {
            // Attachments are excluded from the total too: "3 of 12" is a lie
            // when four of those twelve can never be shown under any setting.
            if (isDraftAttachment(f)) continue;
            total += 1;
            if (isDraft(f)) drafts += 1;
            else if (isChatUpload(f)) chat += 1;
        }
        return { chatUploadCount: chat, draftCount: drafts, totalCount: total };
    }, [files]);

    // Debounced write-behind. Same shape as the chat agent panel's autosave, so
    // a file edited from here and one edited by the agent persist identically.
    const saveFileContent = useMemo(
        () => debounce(async (id, content) => {
            try {
                await updateFileMutation.mutateAsync({ id, updates: { content } });
            } catch {
                /* surfaced by the mutation's onError */
            } finally {
                setIsSavingFile(false);
            }
        }, 1000),
        // Intentionally stable: recreating the debounce would drop pending saves.
        // eslint-disable-next-line
        []
    );

    useEffect(() => () => saveFileContent.cancel(), [saveFileContent]);

    // ─── Handlers ───────────────────────────────────────────────────────────
    // Stable and file-taking, so the memoized cards survive a re-render on every
    // keystroke of a content search.

    const handlePanelContentChange = useCallback((id, content) => {
        setDraftContent(content);
        setIsSavingFile(true);
        saveFileContent(id, content);
    }, [saveFileContent]);

    // Clicking the open file again closes the panel — expected of a
    // list-and-detail layout, and the only way back to a full-width grid.
    const handlePreview = useCallback((file) => {
        setOpenFileId((prev) => (prev === file.id ? null : file.id));
        setDraftContent(null);
    }, []);

    const handleClosePanel = useCallback(() => {
        setOpenFileId(null);
        setDraftContent(null);
    }, []);

    useEffect(() => {
        if (!openFileId) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            handleClosePanel();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [openFileId, handleClosePanel]);

    const handleOpenEditor = useCallback((file) => {
        navigate(`${baseUrl}/editor/${file.id}`);
    }, [navigate, baseUrl]);

    const handleTogglePin = useCallback(async (file) => {
        try {
            await updateFileMutation.mutateAsync({ id: file.id, updates: { pinned: !file.pinned } });
        } catch {
            toast.error('Failed to update pin');
        }
    }, [updateFileMutation]);

    const handleUpdateTags = useCallback(async (file, newTags) => {
        try {
            await updateFileMutation.mutateAsync({ id: file.id, updates: { tags: newTags } });
        } catch {
            toast.error('Failed to update tags');
        }
    }, [updateFileMutation]);

    const handleRename = useCallback(async (file, newName) => {
        if (!newName.trim() || newName === file.name) return;
        try {
            await updateFileMutation.mutateAsync({ id: file.id, updates: { name: newName } });
        } catch {
            toast.error('Rename failed');
        }
    }, [updateFileMutation]);

    const handleSetCategory = useCallback(async (file, category) => {
        if (file.category === category) return;
        try {
            await updateFileMutation.mutateAsync({ id: file.id, updates: { category } });
        } catch {
            toast.error('Failed to move');
        }
    }, [updateFileMutation]);

    const handleDelete = useCallback(async (file) => {
        try {
            await deleteFileMutation.mutateAsync(file.id);
        } catch { /* handled by mutation */ }
    }, [deleteFileMutation]);

    const handleDownload = useCallback((file) => downloadFile(file), []);

    /**
     * Hand artifacts to the chat composer.
     *
     * Route state rather than a query param: Chat replaces the entire query
     * string when it resolves a session, so a `?ref=` would be silently dropped
     * a beat after arriving.
     */
    const handleSendToAdvisor = useCallback((target) => {
        const list = Array.isArray(target) ? target : [target];
        if (!list.length) return;
        navigate(`${baseUrl}/chat`, {
            state: { attachFiles: list.map((f) => ({ type: 'file', id: f.id, name: f.name })) },
        });
    }, [navigate, baseUrl]);

    const handleToggleSelect = useCallback(
        (file, shiftKey) => selection.toggle(file.id, shiftKey),
        [selection]
    );

    // ─── Bulk ───────────────────────────────────────────────────────────────
    const selectedFiles = useMemo(
        () => files.filter((f) => selection.selected.has(f.id)),
        [files, selection.selected]
    );

    // Same partial-failure reporting as the task board's bulk actions: you
    // should never be told "12 moved" when three of them threw.
    const runBulk = useCallback(async (targets, fn, verb) => {
        if (!targets.length) return;
        const results = await Promise.allSettled(targets.map(fn));
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed) toast.error(`${targets.length - failed} ${verb}, ${failed} failed`);
        else toast.success(`${targets.length} artifact${targets.length === 1 ? '' : 's'} ${verb}`);
    }, []);

    const bulkSetPinned = useCallback(async (pinned) => {
        const targets = selectedFiles.filter((f) => !!f.pinned !== pinned);
        if (!targets.length) {
            toast.info(pinned ? 'They are all pinned already' : 'None of those are pinned');
            return;
        }
        await runBulk(
            targets,
            (f) => updateFileMutation.mutateAsync({ id: f.id, updates: { pinned } }),
            pinned ? 'pinned' : 'unpinned'
        );
    }, [selectedFiles, runBulk, updateFileMutation]);

    const bulkSetCategory = useCallback(async (category) => {
        const targets = selectedFiles.filter((f) => f.category !== category);
        if (!targets.length) {
            toast.info('They are all there already');
            return;
        }
        await runBulk(
            targets,
            (f) => updateFileMutation.mutateAsync({ id: f.id, updates: { category } }),
            'moved'
        );
    }, [selectedFiles, runBulk, updateFileMutation]);

    const bulkDownload = useCallback(async () => {
        selectedFiles.forEach(downloadFile);
    }, [selectedFiles]);

    const bulkDelete = useCallback(async () => {
        await runBulk(selectedFiles, (f) => deleteFileMutation.mutateAsync(f.id), 'deleted');
        selection.clear();
    }, [selectedFiles, runBulk, deleteFileMutation, selection]);

    // ─── Drag-and-drop upload ───────────────────────────────────────────────
    const uploadFileFromDrop = useCallback(async (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            let type = 'other';
            let category = 'Docs';

            if (file.type.startsWith('image/')) {
                type = 'asset';
                category = 'Assets';
            } else if (file.name.match(/\.(jsx?|tsx?)$/)) {
                type = 'mockup';
                category = 'Mockups';
            } else if (file.name.match(/\.(md|txt|json|css|html|xml|svg|xsl|xslt|xsd|plist|config)$/)) {
                type = 'doc';
                category = 'Docs';
            }

            try {
                await createFileMutation.mutateAsync({ name: file.name, type, category, content: e.target.result });
                toast.success(`Uploaded: ${file.name}`);
            } catch {
                toast.error(`Failed to upload: ${file.name}`);
            }
        };

        const isText = file.type.startsWith('text/') || file.name.match(/\.(js|jsx|ts|tsx|md|json|css|html|xml|svg|xsl|xslt|xsd|plist|config)$/);
        if (isText) reader.readAsText(file);
        else reader.readAsDataURL(file);
    }, [createFileMutation]);

    // `dataTransfer.items.length > 0` — the old gate — is also true for an
    // in-page pointer drag, so dragging a card between shelf columns raised the
    // upload overlay. Only an OS file drag carries the 'Files' type.
    const isFileDrag = (e) => !!e.dataTransfer?.types?.includes('Files');

    const handleDragEnter = useCallback((e) => {
        if (readOnly || !isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        setIsDragging(true);
    }, [readOnly]);

    const handleDragLeave = useCallback((e) => {
        if (readOnly || !isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current <= 0) {
            dragCounter.current = 0;
            setIsDragging(false);
        }
    }, [readOnly]);

    const handleDragOver = useCallback((e) => {
        if (readOnly || !isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
    }, [readOnly]);

    const handleDrop = useCallback(async (e) => {
        if (readOnly || !isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;

        for (const file of Array.from(e.dataTransfer.files)) {
            await uploadFileFromDrop(file);
        }
    }, [readOnly, uploadFileFromDrop]);

    // ─── Creation ───────────────────────────────────────────────────────────
    const submitFile = useCallback(async (name, type, category, content) => {
        try {
            await createFileMutation.mutateAsync({ name, type, category, content });
            setNewFileName('');
            setUploadFile(null);
            setIsDialogOpen(false);
            toast.success('Created');
        } catch {
            toast.error('Creation failed');
        }
    }, [createFileMutation]);

    const handleCreateFile = useCallback(async (e) => {
        e.preventDefault();
        let type = 'other';
        if (newFileCategory === 'Docs') type = 'doc';
        if (newFileCategory === 'Mockups') type = 'mockup';
        if (newFileCategory === 'Assets') type = 'asset';

        let name = newFileName;
        if (!uploadFile) {
            if (type === 'doc' && !name.endsWith('.md')) name += '.md';
            if (type === 'mockup' && !name.endsWith('.jsx')) name += '.jsx';
        }

        let content = '# New File';
        if (type === 'mockup') content = 'export default function Component() {\n  return <div>New Component</div>\n}';

        if (uploadFile) {
            const reader = new FileReader();
            reader.onload = async (ev) => submitFile(name, type, newFileCategory, ev.target.result);
            const isText = uploadFile.type.startsWith('text/') || uploadFile.name.match(/\.(js|jsx|ts|tsx|md|json|css|html|xml|svg|xsl|xslt|xsd|plist|config)$/);
            if (isText) reader.readAsText(uploadFile);
            else reader.readAsDataURL(uploadFile);
        } else {
            await submitFile(name, type, newFileCategory, content);
        }
    }, [newFileCategory, newFileName, uploadFile, submitFile]);

    // One bundle of card/row props, so the grid, the list and the shelf all
    // wire an artifact up identically.
    //
    // Deliberately free of anything per-file: `openFileId` and the isSelected
    // predicate would change this object's identity every time you opened a
    // file or ticked a box, which re-renders every card on screen and makes the
    // React.memo on them worthless. Those two are passed per-card, already
    // narrowed to a boolean.
    const rowProps = useMemo(() => ({
        query: deferredQuery.trim().toLowerCase(),
        selectionActive: selection.count > 0,
        onOpen: handleOpenEditor,
        onPreview: handlePreview,
        onPin: handleTogglePin,
        onDownload: handleDownload,
        onDelete: handleDelete,
        onRename: handleRename,
        onUpdateTags: handleUpdateTags,
        onToggleSelect: handleToggleSelect,
        onSendToAdvisor: handleSendToAdvisor,
        readOnly,
    }), [
        deferredQuery, selection.count,
        handleOpenEditor, handlePreview, handleTogglePin, handleDownload, handleDelete,
        handleRename, handleUpdateTags, handleToggleSelect, handleSendToAdvisor, readOnly,
    ]);

    // Every hook is above this line. The skeleton branch used to sit near the
    // top, above four useCallbacks — so the render that resolved the query went
    // from 23 hooks to 27 and React threw "rendered more hooks than during the
    // previous render" on any cold reload straight onto this route. Early
    // returns go last.
    if (isLoadingFiles) {
        return <FilesSkeleton />;
    }

    const storedOpenFile = openFileId ? files.find((f) => f.id === openFileId) : null;
    // Draft wins while the debounce is in flight; otherwise the server copy does.
    const openFile = storedOpenFile
        ? (draftContent !== null ? { ...storedOpenFile, content: draftContent } : storedOpenFile)
        : null;

    const isFiltered = deferredQuery.trim().length > 0
        || filters.categories.length > 0
        || filters.tags.length > 0
        || filters.source.length > 0
        || filters.pinnedOnly;

    const bulkSlot = selection.count > 0 ? (
        <FileBulkActions
            count={selection.count}
            categories={categories}
            onPin={() => bulkSetPinned(true)}
            onUnpin={() => bulkSetPinned(false)}
            onSetCategory={bulkSetCategory}
            onDownload={bulkDownload}
            onSendToAdvisor={() => handleSendToAdvisor(selectedFiles)}
            onDelete={bulkDelete}
            onClear={selection.clear}
        />
    ) : null;

    return (
        <>
            {/* A bare flex axis — no chrome of its own. Artifacts stacks its
                surfaces vertically (bar over list) while <main> runs
                horizontally, so it needs a container to turn the axis. It reuses
                the shell's md:gap-3 so nested gaps stay on one rhythm, and
                carries no background, which is what keeps this from being the
                "one big card" the layout moved away from. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col md:gap-3">
                <FileControlBar
                    activeView={viewMode}
                    onViewChange={(v) => startTransition(() => setViewMode(v))}
                    query={search}
                    onQueryChange={setSearch}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sort={sort}
                    onSortChange={setSort}
                    tags={tags}
                    categories={categories}
                    chatUploadCount={chatUploadCount}
                    draftCount={draftCount}
                    shown={visibleFiles.length}
                    total={totalCount}
                    readOnly={readOnly}
                    onNew={() => setIsDialogOpen(true)}
                    bulkSlot={bulkSlot}
                />

                {viewMode === 'shelf' ? (
                    <FileShelf
                        files={visibleFiles}
                        categories={categories}
                        activeCategories={filters.categories}
                        rowProps={rowProps}
                        openFileId={openFileId}
                        isChecked={selection.isSelected}
                        readOnly={readOnly}
                        onSetCategory={handleSetCategory}
                    />
                ) : (
                    <Surface
                        aria-label="Artifacts"
                        className="flex-1"
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        {/* Drag overlay. Fills the surface edge to edge — the
                            surface already supplies the inset and the radius.
                            Dropping onto the artifact panel does nothing, which
                            is right: you're editing a document there, not
                            uploading to it. */}
                        {isDragging && !readOnly && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center border-2 border-dashed border-primary bg-primary/10 backdrop-blur-sm md:rounded-[20px]">
                                <div className="text-center">
                                    <Upload className="mx-auto mb-4 h-16 w-16 animate-bounce text-primary" />
                                    <p className="text-xl font-semibold text-primary">Drop files to upload</p>
                                    <p className="mt-1 text-sm text-muted-foreground">Images, documents, code files</p>
                                </div>
                            </div>
                        )}

                        <ScrollArea className="flex-1">
                            <div key={viewMode} className="animate-in fade-in p-3 pb-32 duration-200 md:p-4">
                                {visibleFiles.length === 0 ? (
                                    <EmptyState
                                        isFiltered={isFiltered}
                                        readOnly={readOnly}
                                        onNew={() => setIsDialogOpen(true)}
                                        onClear={() => { setSearch(''); setFilters(EMPTY_FILTERS); }}
                                    />
                                ) : viewMode === 'grid' ? (
                                    /* auto-fill sizes against this element's width, not the
                                       viewport, so the grid reflows correctly the moment the
                                       panel takes its 480px — and stays correct at every
                                       width during a resize drag. */
                                    <div className="grid grid-cols-2 gap-4 md:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
                                        {visibleFiles.map((file) => (
                                            <FileCard
                                                key={file.id}
                                                {...rowProps}
                                                file={file}
                                                isSelected={openFileId === file.id}
                                                isChecked={selection.isSelected(file.id)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {visibleFiles.map((file) => (
                                            <FileRow
                                                key={file.id}
                                                {...rowProps}
                                                file={file}
                                                isSelected={openFileId === file.id}
                                                isChecked={selection.isSelected(file.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </Surface>
                )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                THE OPENED ARTIFACT

                A peer surface beside the list on desktop, and a full-screen
                takeover on mobile (owned by ArtifactSurface). A stable key
                means picking a different card swaps the contents in place
                instead of animating the whole panel out and back in.
            ═══════════════════════════════════════════════════════════════ */}
            <AnimatePresence>
                {openFile && (
                    <ArtifactSurface
                        key="file-panel"
                        label={openFile.name}
                        resize={{ min: 400, max: 960, initial: 600, storageKey: 'forge:panel:file:v2' }}
                    >
                        <FilePanel
                            file={openFile}
                            projectId={projectId}
                            isSaving={isSavingFile}
                            onContentChange={handlePanelContentChange}
                            onClose={handleClosePanel}
                            onDownload={() => downloadFile(openFile)}
                            onOpenInEditor={() => navigate(`${baseUrl}/editor/${openFile.id}`)}
                            getIcon={getFileIcon}
                            readOnly={readOnly}
                        />
                    </ArtifactSurface>
                )}
            </AnimatePresence>

            {/* Creation. Lives here rather than behind a DialogTrigger inside the
                bar, so the bar stays a pure control strip. */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader><DialogTitle>Create artifact</DialogTitle></DialogHeader>
                    <Tabs defaultValue="blank" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 rounded-xl">
                            <TabsTrigger value="blank" className="rounded-lg">Create blank</TabsTrigger>
                            <TabsTrigger value="upload" className="rounded-lg">Upload</TabsTrigger>
                        </TabsList>
                        <form onSubmit={handleCreateFile} className="mt-4 space-y-4">
                            <TabsContent value="blank" className="space-y-4">
                                <Input
                                    placeholder="filename.md"
                                    value={newFileName}
                                    onChange={(e) => setNewFileName(e.target.value)}
                                    className="rounded-xl"
                                />
                            </TabsContent>
                            <TabsContent value="upload" className="space-y-4">
                                <div className="rounded-xl border-2 border-dashed border-white/10 p-6 text-center transition-colors hover:border-primary/30">
                                    <Input
                                        type="file"
                                        className="hidden"
                                        id="file-upload"
                                        onChange={(e) => {
                                            const f = e.target.files[0];
                                            if (f) { setUploadFile(f); setNewFileName(f.name); }
                                        }}
                                    />
                                    <label htmlFor="file-upload" className="cursor-pointer">
                                        <Upload className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                                        <p className="text-sm text-muted-foreground">
                                            {uploadFile ? uploadFile.name : 'Click to select file'}
                                        </p>
                                    </label>
                                </div>
                            </TabsContent>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Category</label>
                                <Select value={newFileCategory} onValueChange={setNewFileCategory}>
                                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button type="submit" className="w-full rounded-xl">Create</Button>
                        </form>
                    </Tabs>
                </DialogContent>
            </Dialog>
        </>
    );
}

function EmptyState({ isFiltered, readOnly, onNew, onClear }) {
    const Icon = isFiltered ? SearchX : FolderOpen;
    return (
        <div className="flex flex-col items-center justify-center py-20">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-secondary/30">
                <Icon className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">
                {isFiltered ? 'Nothing matches' : 'No artifacts yet'}
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
                {isFiltered
                    ? 'Try a different search, or clear the filters'
                    : 'Create one, or drag and drop to upload'}
            </p>
            {isFiltered ? (
                <Button variant="secondary" onClick={onClear} className="rounded-xl">Clear filters</Button>
            ) : (
                !readOnly && (
                    <Button onClick={onNew} className="rounded-xl">
                        <Plus className="mr-2 h-4 w-4" /> Create artifact
                    </Button>
                )
            )}
        </div>
    );
}

function downloadFile(file) {
    if (!file) return;
    let blob;
    if (file.content.startsWith('data:')) {
        const arr = file.content.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        blob = new Blob([u8arr], { type: mime });
    } else {
        blob = new Blob([file.content], { type: 'text/plain' });
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}
